import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { AuthData, AuthPassedResponse, SignInPageProps, CurrentUser } from 'types';
import { SIGN_IN_HEADER, SIGN_IN_DESCRIPTION } from 'consts';
import {
  countDownNotification,
  formatErrorsForFormNotification,
  getAfterLogInRedirectPath,
  redirectToShopify,
} from 'utils/services';
import { setDataToLocalStore, trackSessionRedirects } from 'utils/localStoreTools';
import { signInFirstStep, signInCheckCode, resetPhoneNumber } from 'api';
import { removeLogInDataFromBrowser } from 'utils/localStoreTools';
import { getResendCodeErrorNotification, safeTextInsert } from 'utils/formatters';
import { useAppDispatch } from 'store/hooks';
import { setUser } from 'store';
import { SignInWrapper } from 'containers/SignWrapper';
import { DynamicForm } from 'components/DynamicForm';
import { HeadComponent } from 'components/HeadComponent';
import { getCheckCodeFields, getSignInFields } from './formFields';
import { Snackbar, SnackbarProps } from 'components/Snackbar';
import { CommentedLink } from 'components/CommentedLink/CommentedLink';

import styles from './SignIn.module.scss';
import { getPageH1Title } from '../../utils/seoFormatters';
import { SeoHiddenContent } from '../../components/SeoHiddenContent';

export const SignIn = ({ signInSlides, shopifySid, redirectUrl, ogData, query }: SignInPageProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState('init');
  const [authData, setAuthData] = useState<AuthData>();
  const [firstStepData, setFirstStepData] = useState<AuthData>({});
  const [userPhone, setUserPhone] = useState('');
  const [notification, setNotification] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState<SnackbarProps | null>(null);
  const [isWaitingResponse, setIsWaitingResponse] = useState(false);

  const handleOpenSnackbar = () => {
    setSnackbarMessage({ header: 'Confirmation link was sent to your e-mail.' });
  };

  const handleCloseSnackbar = () => {
    setSnackbarMessage(null);
  };

  const sendCode = async (data: AuthData) => {
    setIsWaitingResponse(true);
    removeLogInDataFromBrowser();
    const res = await signInFirstStep(data);
    if (res.ok) {
      if (res?.phone) setUserPhone(res.phone);
      if (step === 'check') setSnackbarMessage({ header: 'Code resent successfully.' });
      if (res?.status === 'not_need') {
        return checkCode(data);
      }
      setFirstStepData(data);
      setStep('check');
    } else {
      if (res.seconds_left) {
        countDownNotification({
          fieldName: 'loginError',
          callback: setNotification,
          formatter: getResendCodeErrorNotification,
          message: res.message || '',
          timer: res.seconds_left || 0,
        });
      } else {
        console.log(res)
        setNotification({ loginError: getResendCodeErrorNotification(res, res.seconds_left || 0) });
      }
      if (step === 'check')
        setSnackbarMessage(getResendCodeErrorNotification(res, res.seconds_left || 0));
    }
    setIsWaitingResponse(false);
  };

  const onSignIn = async (data: AuthData) => {
    setAuthData(data);
    await sendCode(data);
  };

  const getAdminRedirectUrl = (user: CurrentUser): string => {
    if (user.role === 'super_admin' || user.permissions?.includes('dashboard_view')) {
      return '/admin-panel/dashboard';
    }
    if (user.permissions?.length) {
      const firstPermissionSlug = user.permissions[0]
        .replace('_view', '')
        .replace('_edit', '')
        .replace('_', '-')
        .replace('users', 'customers')
        .replace('system', 'management');
      return `/admin-panel/${firstPermissionSlug}`;
    }
    return '/account-settings';
  };

  const checkCode = async (data: Record<string, any>) => {
    const checkData = { ...data, ...firstStepData };
    if (shopifySid) {
      checkData.shopify_sid = shopifySid;
    }
    const res = (await signInCheckCode(checkData)) as AuthPassedResponse;
    if (res.ok) {
      if (res.additional && res.additional.shopify_sid && res.additional.shopify_sign) {
        redirectToShopify(res.additional.shopify_sid, res.additional.shopify_sign);
        return;
      }
      const userData: CurrentUser = (res.data as CurrentUser) || {};
      userData && dispatch(setUser({ ...userData }));
      setDataToLocalStore('guestCartToken', null);
      if (userData.role === 'admin' || userData.role === 'super_admin') {
        router.push(redirectUrl || getAdminRedirectUrl(userData)).then();
      } else if (userData.registration_step) {
        router.push(`/sign-up${redirectUrl ? '?redirect=' + redirectUrl : ''}`).then();
      } else {
        if(userData?.id)
          setUser(userData.id)
        trackSessionRedirects(router, () => {
          router.push(getAfterLogInRedirectPath(query)).then();
        });
      }
    } else {
      setNotification(formatErrorsForFormNotification(res, 'phoneCodeErrors'));
    }
  };

  const onResend = async () => {
    authData && (await sendCode(authData));
    !authData && setStep('init');
  };

  const onResetPhone = async () => {
    if (authData?.email) {
      const response = await resetPhoneNumber({ email: authData.email });
      if (response.ok) {
        handleOpenSnackbar();
      } else {
        setNotification(formatErrorsForFormNotification(response, 'phoneCodeErrors'));
      }
    }
  };

  return (
    <>
      <HeadComponent headTitle="Sign In | SENDY" ogData={ogData} />
      <SeoHiddenContent title={getPageH1Title('signIn')} />
      <SignInWrapper slides={signInSlides} ogData={ogData}>
        <div className={styles.container}>
          <div className={styles.contentWrapper}>
            {step === 'init' ? (
              <>
                <DynamicForm
                  buttonCaption="Sign In"
                  description={SIGN_IN_DESCRIPTION}
                  disableButton={isWaitingResponse}
                  buttonId="sign-in-page-btn"
                  fields={getSignInFields(safeTextInsert(query?.redirect))}
                  header={SIGN_IN_HEADER}
                  marginMode="none"
                  notifications={notification}
                  onSend={onSignIn}
                />
                <div className={styles.mobileLinks}>
                  <CommentedLink
                    caption="Don't have an account?"
                    linkId="sign-in-page-sign-up-mobile"
                    linkHref="/sign-up"
                    linkCaption="Sign Up"
                  />
                </div>
              </>
            ) : (
              <DynamicForm
                onSend={checkCode}
                fields={getCheckCodeFields(onResend, userPhone, onResetPhone)}
                buttonCaption="Continue"
                marginMode="none"
                notifications={notification}
              />
            )}
          </div>
        </div>
      </SignInWrapper>
      {snackbarMessage && <Snackbar {...snackbarMessage} onClose={handleCloseSnackbar} />}
    </>
  );
};
